import { isEmpty } from "ramda";
import { useMemo, useRef, useState } from "react";
import { Autocomplete, Button, Loading, Modal, Spinner, TextInput, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import { useCreateVehicleMutation, useReactivateVehicleMutation, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast } from "@/utils";
import { VehicleVerificationErrorAlert } from "../common/VehicleVerificationErrorAlert";
import { getVerificationErrorType } from "../helpers";
import { ConfirmVehicleModal } from "./ConfirmVehicleModal";
import { VehicleInArchiveModal } from "./VehicleInArchiveModal";

interface AddVehicleModalProps {
  open: boolean;
  onClose: () => void;
}

type StateOption = { name: string; value: string };

/**
 * The plate-based add-vehicle front door, and the AU default.
 *
 * Australia only, by construction rather than by choice: the plate is resolved against a vehicle
 * registry, and AU is the only country we have one for. AddVehicleEntry routes here when KYV is
 * off, which it cannot be outside AU — see useKyvEnabled.
 *
 * Still the only flow that handles the cases the VIN path has no answer for: `dry_run`
 * verification before anything is created, the vehicle-already-exists collision, and reactivating
 * an archived vehicle instead of adding a second copy of it.
 */
export const AddVehicleModal = ({ open, onClose }: AddVehicleModalProps) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const { country } = useOperator();
  const { isFetching, data: response } = useGetCountryStatesQuery(country);
  const [verifyVehicle, { isLoading, error, reset }] = useCreateVehicleMutation();
  const [reactivateVehicle, { isLoading: isReactivating }] = useReactivateVehicleMutation();
  const [numberPlate, setNumberPlate] = useState<string>("");
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [query, setQuery] = useState<string>("");
  const [pendingVehicle, setPendingVehicle] = useState<Vehicle | undefined>(undefined);

  const options = useMemo(
    () => response?.data.states.map((state) => ({ name: state.name, value: state.id })) ?? [],
    [response?.data.states]
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const errorType = useMemo(() => {
    if (!error) return undefined;
    return getVerificationErrorType(error);
  }, [error]);

  const isArchivedError = Boolean(errorType && errorType.includes("vehicle-archived"));
  const archivedVehicleUuid = isArchivedError ? errorType!.replace("vehicle-archived-", "") : undefined;

  const handleClose = () => {
    setNumberPlate("");
    setSelectedState(null);
    setQuery("");
    setPendingVehicle(undefined);
    reset();
    onClose();
  };

  const handleReactivate = () => {
    if (!archivedVehicleUuid) return;

    reactivateVehicle(archivedVehicleUuid)
      .unwrap()
      .then(() => {
        addToast("info", `Vehicle has been successfully reactivated`);
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
        handleClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!numberPlate || !selectedState) return;

    verifyVehicle({
      dry_run: true,
      registration_number: numberPlate,
      state_uuid: selectedState.value,
    })
      .unwrap()
      .then((res) => {
        setPendingVehicle(res);
      })
      .catch(() => {
        console.warn("Failed to verify vehicle");
      });
  };

  return (
    <>
      <Modal open={open && !pendingVehicle && !isArchivedError} onClose={handleClose} className="w-[85vw] !p-6 md:w-[400px] lg:w-[400px]">
        <div className="flex min-h-[500px] flex-col">
          <Typography variant="title">Add Vehicle</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            Enter the vehicle details below
          </Typography>

          <div className="relative mt-5 flex flex-1 flex-col">
            {isLoading && <Loading />}

            <form className="flex flex-1 flex-col" onSubmit={handleSubmit}>
              <label htmlFor="modal-number-plate" className="flex flex-col gap-1">
                <Typography variant="paragraph">Vehicle Plate Number</Typography>
                <TextInput
                  title="Only Alphabets and Numbers are allowed"
                  pattern="[a-zA-Z0-9\-]+"
                  placeholder="Enter Vehicle Plate Number"
                  id="modal-number-plate"
                  value={numberPlate}
                  onChange={(e) => setNumberPlate(e.target.value)}
                  hasError={errorType === "verification-failed" || errorType === "no-match"}
                  hasWarning={errorType === "vehicle-exists" || errorType === "vehicle-exists-duplicate"}
                  required
                  autoFocus
                  ref={inputRef}
                  maxLength={32}
                />
              </label>
              <label htmlFor="modal-state" className="mt-5 flex flex-col gap-1">
                <Typography variant="paragraph">Select the State of Your Vehicle</Typography>
                <Autocomplete<StateOption>
                  className="w-full"
                  placeholder="Search State"
                  options={filteredOptions}
                  value={selectedState}
                  onValueChange={setSelectedState}
                  onQueryChange={setQuery}
                  isLoading={isFetching}
                  disabled={isEmpty(options) || isFetching}
                />
              </label>

              {errorType && <VehicleVerificationErrorAlert errorType={errorType} />}

              <div className="mt-auto flex flex-col gap-3">
                <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading || !numberPlate || !selectedState}>
                  {isLoading && <Spinner />}
                  Find Vehicle
                </Button>
                <Button type="button" variant="secondary" size="lg" className="w-full" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
      <ConfirmVehicleModal
        vehicle={pendingVehicle}
        onCancel={() => {
          setPendingVehicle(undefined);
          reset();
        }}
        onClose={handleClose}
      />
      <VehicleInArchiveModal
        open={Boolean(errorType && errorType.includes("vehicle-archived"))}
        onClose={() => reset()}
        onReactivate={handleReactivate}
        isReactivating={isReactivating}
      />
    </>
  );
};
